/**
 * Perfiles locales (revisión de ADR OQ-1, 2026-09-10): IndexedDB local es la
 * fuente de autoridad; cada perfil es una partición de datos completamente
 * aislada, identificada por un id estable (FR-PROFILE-*, spec/03-data-model.md).
 *
 * Sin usuario online ni contraseña de servidor: un perfil es solo un nombre y
 * un identificador local, sin autenticación — no hay backend con el que
 * autenticarse. El registro de perfiles vive en su propio IndexedDB, separado
 * de los datos de cada perfil (que viven en `profileDataDbName(profileId)`).
 */

import { generateId } from "../core/ids.js";
import type { LocalStore } from "../storage/local-store.js";

export const PROFILES_COLLECTION = "profiles";
const ACTIVE_PROFILE_META_KEY = "active_profile_id";
export const PROFILE_REGISTRY_DB_NAME = "finvoz-profiles";

export interface Profile {
  readonly profileId: string;
  readonly name: string;
  readonly createdAt: string; // instante UTC ISO-8601
}

export class InvalidProfileNameError extends Error {
  constructor(name: string) {
    super(`Nombre de perfil inválido: "${name}". No puede estar vacío.`);
    this.name = "InvalidProfileNameError";
  }
}

export class ProfileNotFoundError extends Error {
  constructor(profileId: string) {
    super(`No existe ningún perfil con id ${profileId}`);
    this.name = "ProfileNotFoundError";
  }
}

export class CannotDeleteLastProfileError extends Error {
  constructor() {
    super("No se puede eliminar el único perfil restante: FinVoz siempre necesita al menos un perfil activo.");
    this.name = "CannotDeleteLastProfileError";
  }
}

/** Nombre de la base de datos IndexedDB de un perfil, aislada del resto (FR-PROFILE-2). */
export function profileDataDbName(profileId: string): string {
  return `finvoz-data-${profileId}`;
}

export async function createProfile(
  registry: LocalStore,
  name: string,
  now: string = new Date().toISOString(),
  profileId: string = generateId(),
): Promise<Profile> {
  const trimmed = name.trim();
  if (!trimmed) throw new InvalidProfileNameError(name);
  const profile: Profile = { profileId, name: trimmed, createdAt: now };
  await registry.putBatch(PROFILES_COLLECTION, [{ id: profile.profileId, value: profile }]);
  return profile;
}

export async function listProfiles(registry: LocalStore): Promise<Profile[]> {
  const items = await registry.list<Profile>(PROFILES_COLLECTION);
  return items.map((i) => i.value).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function getActiveProfileId(registry: LocalStore): Promise<string | undefined> {
  return registry.readMeta<string>(ACTIVE_PROFILE_META_KEY);
}

export async function selectProfile(registry: LocalStore, profileId: string): Promise<void> {
  const existing = await registry.get<Profile>(PROFILES_COLLECTION, profileId);
  if (!existing) throw new ProfileNotFoundError(profileId);
  await registry.writeMeta(ACTIVE_PROFILE_META_KEY, profileId);
}

export interface DeleteProfileResult {
  readonly deleted: Profile;
  readonly wasActive: boolean;
  /** Perfil que queda activo tras el borrado (definido solo si `wasActive` era true). */
  readonly nextActiveProfileId: string | undefined;
}

/**
 * Elimina un perfil del registro. No borra por sí sola la base de datos de
 * datos del perfil (`profileDataDbName`) — eso requiere `indexedDB.deleteDatabase`,
 * una operación de navegador real que no se ejecuta en el entorno de tests de
 * este núcleo (igual que `IndexedDbLocalStore`, spec/03-data-model.md §8).
 * El llamador (capa de UI, en navegador real) es responsable de purgar esos datos.
 */
export async function deleteProfile(registry: LocalStore, profileId: string): Promise<DeleteProfileResult> {
  const all = await listProfiles(registry);
  const existing = all.find((p) => p.profileId === profileId);
  if (!existing) throw new ProfileNotFoundError(profileId);
  if (all.length <= 1) throw new CannotDeleteLastProfileError();

  await registry.delete(PROFILES_COLLECTION, profileId);

  const activeId = await getActiveProfileId(registry);
  const wasActive = activeId === profileId;
  let nextActiveProfileId: string | undefined;
  if (wasActive) {
    const remaining = all.filter((p) => p.profileId !== profileId);
    nextActiveProfileId = remaining[0]?.profileId;
    if (nextActiveProfileId) await registry.writeMeta(ACTIVE_PROFILE_META_KEY, nextActiveProfileId);
  }
  return { deleted: existing, wasActive, nextActiveProfileId };
}

/**
 * Bootstrap de arranque: garantiza que exista al menos un perfil y que uno
 * esté activo. Primer arranque real: crea "Perfil principal" y lo selecciona.
 */
export async function ensureActiveProfile(registry: LocalStore, defaultName = "Perfil principal"): Promise<Profile> {
  const activeId = await getActiveProfileId(registry);
  if (activeId) {
    const active = await registry.get<Profile>(PROFILES_COLLECTION, activeId);
    if (active) return active;
  }
  const all = await listProfiles(registry);
  if (all.length > 0) {
    await selectProfile(registry, all[0]!.profileId);
    return all[0]!;
  }
  const created = await createProfile(registry, defaultName);
  await selectProfile(registry, created.profileId);
  return created;
}
